// Date utility functions for trade entry.
//
// Dates that belong on a book (trade date, settle date, effective date, as-of date) come from
// the tenant book's business date and the market calendars, both supplied by the server and held
// in utils/businessDate. The browser clock is for timestamps only.

import {
  addBusinessDays, currentBusinessDate, defaultTradeDate, fromIso, isBusinessDayOn, isWeekendIso,
  onOrAfter, toIso,
} from './businessDate';

const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Get the tenant's configured timezone from localStorage.
 * Falls back to America/New_York if not available.
 */
export const getTenantTimezone = (): string => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.timezone) return user.timezone;
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_TIMEZONE;
};

/**
 * Get today's date string (YYYY-MM-DD) in a specific timezone, by the wall clock.
 */
export const getDateInTimezone = (timezone: string): string => {
  const now = new Date();
  const localized = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  return toIso(localized);
};

/**
 * The calendar date on the wall clock in the tenant's timezone. For timestamps, file names and
 * "generated on" labels. Never for a date that goes on a book.
 */
export const getWallClockToday = (): string => getDateInTimezone(getTenantTimezone());

/**
 * The tenant book's current business date. This is "today" for every screen: on a Sunday
 * night it is Monday, and it changes only when the end-of-day run rolls the book.
 */
export const getTenantToday = (): string => currentBusinessDate(getWallClockToday());

/**
 * The date a new ticket defaults to: the business date, or the next one once the end-of-day
 * run has started. Pass the instrument's calendars to land on a day that instrument trades.
 */
export const getTodayDate = (calendars?: string[]): string => defaultTradeDate(getWallClockToday(), calendars);

/**
 * The business day after the default trade date, on the instrument's calendars. The calendars are
 * required: a settle date counted on the book's own calendar lands on bond and loan holidays.
 * Prefer utils/settlement for settle dates.
 */
export const getTomorrowDate = (calendars: string[]): string =>
  addBusinessDays(getTodayDate(calendars), 1, calendars);

/** The business day after a date, on the instrument's calendars (required, as above). */
export const getNextDayDate = (dateString: string, calendars: string[]): string =>
  addBusinessDays(dateString, 1, calendars);

export const parseDateString = (dateString: string): Date => {
  // Parse YYYY-MM-DD string in local timezone
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    return fromIso(getTenantToday());
  }
  return fromIso(dateString);
};

export const formatDateString = (date: Date): string => toIso(date);

export const isWeekend = (date: Date): boolean => isWeekendIso(toIso(date));

/** A business day on the given calendars, or on the book's own calendar when none is given. */
export const isBusinessDay = (date: Date, calendars?: string[]): boolean =>
  isBusinessDayOn(toIso(date), calendars);

export const getNextBusinessDay = (date: Date, calendars?: string[]): string =>
  addBusinessDays(toIso(date), 1, calendars);

/** The date itself when it is a business day, otherwise the next one. */
export const adjustToBusinessDay = (dateString: string, calendars?: string[]): string =>
  onOrAfter(dateString, calendars);

/**
 * Settlement date from trade date and convention (T+0, T+1, T+2, ...), counted in business
 * days on the instrument's calendars. The server recomputes it when the ticket is saved.
 */
export const calculateSettlementDate = (tradeDateString: string, settlementConvention: string,
  calendars: string[]): string => {
  if (!tradeDateString) {
    return '';
  }
  let days = 1;
  const convention = (settlementConvention || '').trim().toUpperCase();
  if (convention) {
    const match = convention.match(/^T?\s*\+?\s*(\d+)$/);
    if (match) days = parseInt(match[1], 10);
  }
  return addBusinessDays(tradeDateString, days, calendars);
};


/**
 * F-NS-015: format a date-or-timestamp for ABF workspace display in the tenant's
 * calendar. Date-only values (YYYY-MM-DD) render as-is; full timestamps (which the
 * API serialises in UTC) are converted to the tenant timezone before formatting so
 * an evening-ET event no longer shows the next UTC day.
 */
export const formatAbfDate = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Not available';
  const raw = String(value);
  // date-only: no time component -> already a calendar date, render directly
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      .format(new Date(`${raw.trim()}T00:00:00`));
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: getTenantTimezone(),
  }).format(d);
};
